import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Snackbar,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import { format } from 'date-fns';
import { VisitRecord, VisitFilters as VisitFiltersType } from '../types/VisitRecord';
import { visitApi } from '../services/api';
import VisitFilters from './VisitFilters';

const ArchivedVisitsTab: React.FC = () => {
  const [visits, setVisits] = useState<VisitRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<VisitFiltersType>({
    startTime: null,
    endTime: null,
    organizationId: '',
    msisdn: '',
    visitToken: '',
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadVisits = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.startTime) params.startTime = filters.startTime.toISOString();
      if (filters.endTime) params.endTime = filters.endTime.toISOString();
      if (filters.organizationId) params.organizationId = filters.organizationId;
      if (filters.msisdn) params.msisdn = filters.msisdn;
      if (filters.visitToken) params.visitToken = filters.visitToken;

      const data = await visitApi.getArchivedVisits(params);
      setVisits(data);
    } catch (error) {
      console.error('Failed to load visits:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load archived visits',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadVisits();
  }, [loadVisits]);

  const handleClearFilters = () => {
    setFilters({
      startTime: null,
      endTime: null,
      organizationId: '',
      msisdn: '',
      visitToken: '',
    });
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const columns: GridColDef[] = [
    {
      field: 'visit_token',
      headerName: 'Visit Token',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value} size="small" color="default" variant="outlined" />
      ),
    },
    {
      field: 'organization_id',
      headerName: 'Organization',
      width: 150,
    },
    {
      field: 'premises_id',
      headerName: 'Premises',
      width: 150,
    },
    {
      field: 'msisdn',
      headerName: 'MSISDN',
      width: 150,
      renderCell: (params: GridRenderCellParams) => params.value || 'N/A',
    },
    {
      field: 'entry_time',
      headerName: 'Entry Time',
      width: 180,
      renderCell: (params: GridRenderCellParams) =>
        format(new Date(params.value), 'yyyy-MM-dd HH:mm:ss'),
    },
    {
      field: 'exit_time',
      headerName: 'Exit Time',
      width: 180,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? format(new Date(params.value), 'yyyy-MM-dd HH:mm:ss') : 'N/A',
    },
    {
      field: 'visit_duration_seconds',
      headerName: 'Duration',
      width: 120,
      renderCell: (params: GridRenderCellParams) => formatDuration(params.value),
    },
    {
      field: 'exit_reason',
      headerName: 'Exit Reason',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const reason = params.value || 'N/A';
        const color = reason === 'manual_close' ? 'warning' : 'default';
        return <Chip label={reason} size="small" color={color} />;
      },
    },
    {
      field: 'archived_at',
      headerName: 'Archived At',
      width: 180,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? format(new Date(params.value), 'yyyy-MM-dd HH:mm:ss') : 'N/A',
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Archived Visits</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadVisits}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <VisitFilters
        filters={filters}
        onFiltersChange={setFilters}
        onSearch={loadVisits}
        onClear={handleClearFilters}
      />

      <DataGrid
        rows={visits}
        columns={columns}
        getRowId={(row) => row.visit_id}
        loading={loading}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        autoHeight
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ArchivedVisitsTab;
